if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        dots: this.querySelector('[data-product-media-dots]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.setupDots();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const trigger = mediaToSwitch.querySelector('button, [role="button"]');
        if (!trigger) return;
        trigger.addEventListener('click', (event) => {
          if (mediaToSwitch.classList.contains('product__thumbnail-more')) {
            event.preventDefault();
            this.expandThumbnails(mediaToSwitch);
            return;
          }
          this.setActiveMedia(mediaToSwitch.dataset.target, false, true);
        });
        mediaToSwitch.addEventListener('mouseenter', () => {
          if (this.mql.matches) this.setActiveMedia(mediaToSwitch.dataset.target, false, false);
        });
        mediaToSwitch.addEventListener('focusin', () => {
          if (this.mql.matches) this.setActiveMedia(mediaToSwitch.dataset.target, false, false);
        });
      });
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();
    }

    setupDots() {
      if (!this.elements.dots || !this.elements.viewer) return;

      this.elements.dots.querySelectorAll('[data-product-media-dot]').forEach((dot) => {
        dot.addEventListener('click', () => {
          const slideIndex = Number(dot.dataset.productMediaDot);
          this.goToDotSlide(slideIndex);
        });
      });

      if (this.elements.viewer.slider) {
        this.elements.viewer.slider.addEventListener('scroll', () => {
          if (this.dotScrollTicking) return;

          this.dotScrollTicking = true;
          window.requestAnimationFrame(() => {
            this.dotScrollTicking = false;
            this.setActiveDot(this.getCurrentDotIndex());
          });
        }, { passive: true });
      }
    }

    goToDotSlide(slideIndex) {
      if (!this.elements.viewer.slider) return;

      const slides = this.elements.viewer.sliderItemsToShow || Array.from(this.elements.viewer.querySelectorAll('.product__media-item')).filter((slide) => slide.clientWidth > 0);
      const targetSlide = slides[slideIndex];
      if (!targetSlide) return;

      this.preventStickyHeader();
      this.elements.viewer.slider.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
      this.setActiveDot(slideIndex);
    }

    getCurrentDotIndex() {
      if (!this.elements.dots || !this.elements.viewer.slider) return 0;

      const dots = this.elements.dots.querySelectorAll('[data-product-media-dot]');
      const maxIndex = dots.length - 1;
      const slider = this.elements.viewer.slider;
      const maxScrollLeft = slider.scrollWidth - slider.clientWidth;

      if (maxIndex < 0) return 0;
      if (maxScrollLeft > 0 && slider.scrollLeft >= maxScrollLeft - 2) return maxIndex;

      const slideOffset = this.elements.viewer.sliderItemOffset || 1;
      const slideIndex = Math.round(slider.scrollLeft / slideOffset);

      return Math.min(Math.max(slideIndex, 0), maxIndex);
    }

    setActiveDot(slideIndex) {
      if (!this.elements.dots) return;

      const dots = this.elements.dots.querySelectorAll('[data-product-media-dot]');
      if (!dots.length) return;

      const activeIndex = Math.min(Math.max(Number(slideIndex) || 0, 0), dots.length - 1);

      if (this.activeDotIndex === activeIndex) return;
      this.activeDotIndex = activeIndex;

      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
          dot.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    expandThumbnails(thumbnail) {
      const thumbnailList = thumbnail.closest('.thumbnail-list');
      if (!thumbnailList) return;

      thumbnailList.classList.add('product__thumbnails-expanded');
      thumbnail.setAttribute('hidden', '');

      const firstExtraThumbnail = thumbnailList.querySelector('.product__thumbnail--desktop-extra[data-target]');
      if (firstExtraThumbnail) {
        this.setActiveMedia(firstExtraThumbnail.dataset.target, false, true);
      }
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      if (event.detail.currentPage != undefined) {
        this.setActiveDot(this.getCurrentDotIndex());
      }

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        const button = element.querySelector('button');
        if (button) button.removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      const button = thumbnail.querySelector('button');
      if (button) button.setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
